import type { UserStatus } from "@/lib/api-types";
import { USER_STATUS_LABELS } from "@/lib/enum-labels";

/**
 * The four badge fills DESIGN.md § Components `badge-status` names, and
 * nothing else — a fifth invented fill would be a spine change, not a story
 * decision.
 */
export type BadgeFill = "neutral" | "attention" | "overdue" | "settled";

/**
 * The one domain-status-to-fill map (DESIGN.md § Components: "The mapping
 * from domain status to fill is defined once, in EXPERIENCE.md, and read
 * from `GET /meta/enums`... no component defines its own status list.").
 * EXPERIENCE.md's own status-to-badge table names lease/bill/compliance
 * statuses, not `UserStatus` — this story's user list is the first screen
 * to need a mapping for `invited`/`active`, so it is recorded here, once,
 * following the same rule: nowhere else in the codebase may branch on
 * these two literal strings to decide a color.
 *
 * Neither `invited` nor `active` fits `attention`/`overdue`/`settled`'s
 * documented meanings (a deadline approaching, something overdue, or an
 * event completed) — an invitation has no expiry surfaced anywhere in this
 * UI and "active" is simply the ordinary, ongoing state of an account, not
 * an achievement to celebrate (DESIGN.md: "PropDesk does not congratulate").
 * Both map to `neutral`, DESIGN.md's own words for "most badges in the
 * product" and the honest choice absent a specified urgency.
 */
export const USER_STATUS_FILL: Record<UserStatus, BadgeFill> = {
  invited: "neutral",
  active: "neutral",
};

/**
 * `GET /meta/enums`'s `statuses` list is the set of values that may ever
 * arrive (AD-2) — this function still resolves fill/label per value from
 * the one map above, but never crashes on a status this map hasn't been
 * updated for yet: a badge rendering as neutral with its own raw value as
 * the label is a far smaller defect than a blank row or a thrown error on
 * an admin list a family member is looking at.
 */
export function badgeFor(status: UserStatus): { label: string; fill: BadgeFill } {
  const fill = USER_STATUS_FILL[status] ?? "neutral";
  const label = USER_STATUS_LABELS[status] ?? status;
  return { label, fill };
}
